import React, { useMemo, useContext, useEffect, useRef, useState } from "react";
import Header3 from "../components/Header3";
import Footer2 from "../components/Footer2";
import Footer3 from "../components/Footer3";
import { Form, Modal } from "react-bootstrap";
import { useLocation } from "react-router-dom";
import { useApp } from "../context/appContext";
import { useAuth } from "../context/auth";
import { useNavigate } from "react-router-dom";
import axios from "../AxiosInstance";
import { message } from "antd";
import { LanguageContext } from "../context/LanguageContext";
import toast from "react-hot-toast";
import { ChatState } from "../context/ChatProvider";
import { motion } from "framer-motion";
import { BookOpen, Megaphone } from "lucide-react";
import {
  Check,
  ChevronLeft,
  ChevronRight,
  Plus,
  X,
  Sparkles,
} from "lucide-react";

const PRIMARY = "#34c3c9";

// Reusable UI ---------------------------------------------------------------
const Title = ({ children }) => (
  <h2 className="text-center text-[22px] font-bold tracking-tight text-slate-900 sm:text-2xl">
    {children}
  </h2>
);

const Sub = ({ children }) => (
  <p className="mx-auto max-w-xl text-center text-sm text-slate-600">
    {children}
  </p>
);

const CTA = ({ children, className = "", ...props }) => (
  <button
    className={`inline-flex items-center justify-center gap-2 rounded-xl px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:brightness-95 ${className}`}
    style={{ background: PRIMARY }}
    {...props}
  >
    {children}
  </button>
);

const Ghost = ({ children, className = "", ...props }) => (
  <button
    className={`inline-flex items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-800 hover:bg-slate-50 ${className}`}
    {...props}
  >
    {children}
  </button>
);

const SegButton = ({ active, children, className = "", ...props }) => (
  <button
    className={`rounded-xl border px-4 py-2 text-sm font-semibold transition ${
      active
        ? "border-transparent text-white"
        : "border-slate-300 text-slate-800 hover:bg-slate-50"
    } ${className}`}
    style={active ? { background: PRIMARY } : undefined}
    {...props}
  >
    {children}
  </button>
);

const Chip = ({ children, onRemove }) => (
  <span
    className="group inline-flex items-center gap-1 rounded-full bg-[var(--p)]/12 px-3 py-1 text-sm font-medium text-[var(--p)] ring-1 ring-[var(--p)]/20"
    style={{ ["--p"]: PRIMARY }}
  >
    {children}
    <button
      aria-label="Remove"
      onClick={onRemove}
      className="-mr-1 rounded-full p-0.5 text-slate-500 transition hover:bg-slate-100"
    >
      <X size={14} />
    </button>
  </span>
);

// Progress ------------------------------------------------------------------
const Progress = ({ step, total }) => (
  <div className="mx-auto mb-5 mt-1 flex w-full max-w-xl items-center gap-2">
    {Array.from({ length: total }).map((_, i) => (
      <div
        key={i}
        className="h-2 flex-1 overflow-hidden rounded-full bg-slate-200"
      >
        <div
          className={`h-full ${i < step ? "bg-[var(--p)]" : "bg-transparent"}`}
          style={{ ["--p"]: PRIMARY }}
        />
      </div>
    ))}
  </div>
);
function Card({ children, className = "" }) {
  return (
    <div
      className={`rounded-2xl shadow-lg hover:shadow-xl transition-shadow ${className}`}
    >
      {children}
    </div>
  );
}

// Local CardContent Component
function CardContent({ children, className = "" }) {
  return <div className={`p-[24px] sm:p-[32px] ${className}`}>{children}</div>;
}

// Local Button Component
function Button({ children, className = "", ...props }) {
  return (
    <button
      className={`inline-flex text-[14px] items-center justify-center  rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 ${className}`}
      {...props}
    >
      {children}
    </button>
  );
}

const AmbassadorAll = {
  english: {
    QAmbassText: `What is an Ambassador?`,
    AAmbassText: `An Ambassador is a coordinator, a guide, and a community builder, inspiring others to connect over books. As an Ambassador, you will organize and lead Book Clubs or Silent Readings, leading discussions and creating memorable experiences in unique settings.`,

    BAmbassText: `Why becoming an Ambassador?`,
    ShareText: `Share your passion:`,
    ShareInfotext: `Use your love of reading to bring people together and make a difference.`,
    EarnText: `Earn rewards:`,
    EarnInfoText: ` Join our loyalty program, unlock new levels based on your engagement and collected points, and earn rewards.`,
    BuildText: `Build your community:`,
    BuildInfoText: ` Meet fellow book enthusiasts and create lasting friendships.`,
    LastBText: `If you love books and enjoy bringing people together, start your journey as an Ambassador today: this is your opportunity to shine!`,
    BAmbssdBtnText: `Become Ambassador`,

    FRModel: `Ready to become an Ofellio Ambassador?`,
    SModel: `Start the process answering a few simple questions!`,
    SBtn: `Start`,

    BAmbssdAn: `Become An Ambassador`,
    AvrgText: `Your average number of books read in a year is`,
    UpToMoreText: ["Up to 3", "Up to 6", "Up to 12", "More than 12"],

    YourText: `You are a `,
    YourArray: ["Student", "Worker", "Retired", "Other"],

    YourFavrText: `Your favorite genres are `,
    TypeGerne: `Type genre`,

    LedText: `Have you led any book clubs in the past?`,
    YesNoText: ["Yes", "No"],

    DiscussText: `Explain in few a lines how you would organize a book club event (e.g. location, how to guide the discussion etc)`,

    WishText: `Explain in a few lines why you wish to become an Ofellio Ambassador`,
    SubmitBtn: `Submit`,

    LTModel: `Thank you for submitting your answers!`,
    ReciveText: `You will receive a confirmation e-mail soon with the Ambassador's guidelines.`,
  },
  italiano: {
    QAmbassText: `Che cos'è un Ambassador?`,
    AAmbassText: `Un Ambassador è un coordinatore e una guida, capace di ispirare gli altri a connettersi attraverso i libri. Come Ambassador, organizzerai e guiderai Book Club o Silent Reading, facilitando le discussioni e creando esperienze coinvolgenti in ambienti unici`,

    BAmbassText: `Perché diventare Ambassador?`,
    ShareText: `Condividi la tua passione:`,
    ShareInfotext: `Usa il tuo amore per la lettura per unire le persone e fare la differenza.`,
    EarnText: `Guadagna premi:`,
    EarnInfoText: ` Unisciti al nostro programma di fedeltà, sblocca nuovi livelli in base al tuo impegno e ai punti accumulati, e guadagna premi.`,
    BuildText: `Costruisci la tua comunità:`,
    BuildInfoText: ` Incontra altri appassionati di libri e crea amicizie durature.`,
    LastBText: `Se ami i libri e ti piace unire le persone, inizia oggi il tuo percorso come Ambassador: questa è la tua opportunità per brillare!`,
    BAmbssdBtnText: `Diventa Ambassador`,

    FRModel: `Pronto a diventare un Ambassador Ofellio?`,
    SModel: `Inizia il processo rispondendo a qualche semplice domanda!`,
    SBtn: `Inizia`,

    BAmbssdAn: `Diventa Un Ambassador`,
    AvrgText: `Il tuo numero medio di libri letti in un anno è`,
    UpToMoreText: ["fino a 3", "fino a 6", "fino a 12", "Più di 12"],

    YourText: `Sei un/una`,
    YourArray: ["Studente", "Lavoratore", "Pensionato", "Altro"],

    YourFavrText: `I tuoi generi preferiti sono `,
    TypeGerne: `Inserisci genere`,

    LedText: `Hai mai condotto dei book clubs in passato?`,
    YesNoText: ["Si", "No"],

    DiscussText: `Spiega in poche righe come organizzeresti un evento di book club (ad esempio, la location, come guidare la discussione, ecc.)`,

    WishText: `Spiega in poche righe perché desideri diventare un Ofellio Ambassador.`,
    SubmitBtn: `Invia`,

    LTModel: `Grazie per aver inviato le tue risposte!`,
    ReciveText: `Riceverai presto una e-mail di conferma con le linee guida dell'Ambassador`,
  },
  deutsch: {
    QAmbassText: `Was ist ein Ambassador?`,
    AAmbassText: `Ein Ambassador ist ein Organisator und Mentor, der Menschen inspiriert, sich über Bücher zu verbinden. Als Ambassador planst und leitest du Book Clubs oder Silent Readings, moderierst Diskussionen und schaffst unvergessliche Erlebnisse in besonderen Umgebungen.`,

    BAmbassText: `Warum Ambassador werden?`,
    ShareText: `Teile deine Leidenschaft:`,
    ShareInfotext: `     Nutze deine Liebe zum Lesen, um Menschen zusammenzubringen und einen Unterschied zu machen.`,
    EarnText: `Verdiene Belohnungen:`,
    EarnInfoText: ` Tritt unserem Treueprogramm bei, schalte neue Levels basierend auf deinem Engagement und den gesammelten Punkten frei und verdiene Belohnungen.`,
    BuildText: `Baue deine Community auf:`,
    BuildInfoText: ` Triff andere Buchliebhaber und schaffe langfristige Freundschaften.`,
    LastBText: `Wenn du Bücher liebst und es genießt, Menschen zusammenzubringen, beginne noch heute deine Reise als Ambassador: Dies ist deine Gelegenheit, zu glänzen!`,
    BAmbssdBtnText: `Werde Ambassador`,

    FRModel: `Bereit, ein Ofellio Ambassador zu werden?`,
    SModel: `Starte den Prozess, indem du ein paar einfache Fragen beantwortest!`,
    SBtn: `Starten`,

    BAmbssdAn: `Werde ein Ambassador`,
    AvrgText: `Deine durchschnittliche Anzahl an gelesenen Büchern pro Jahr ist`,
    UpToMoreText: ["bis zu 3", "bis zu 6", "bis zu 12", "Mehr als 12"],

    YourText: `Du bist ein/e`,
    YourArray: ["Student", "Arbeiter", "Rentner", "Andere"],

    YourFavrText: `Deine Lieblingsgenres sind `,
    TypeGerne: `Gib Genre ein`,

    LedText: `Hast du in der Vergangenheit schon mal Book Clubs geleitet?`,
    YesNoText: ["Ja", "Nein"],

    DiscussText: `Erkläre in ein paar Sätzen, wie du ein Book Club-Event organisieren würdest (z. B. Ort, wie du die Diskussion leitest, etc.).`,

    WishText: `Erkläre in ein paar Sätzen, warum du Ofellio Ambassador werden möchtest.`,
    SubmitBtn: `Senden`,

    LTModel: `Danke, dass du deine Antworten eingereicht hast!`,
    ReciveText: ` Du wirst bald eine Bestätigungs-E-Mail mit den Richtlinien des Ambassadors erhalten`,
  },
  español: {
    QAmbassText: `¿Qué es un Ambassador?`,
    AAmbassText: `Un Ambassador es un organizador y un guía que inspira a otros a conectar a través de los libros. Como Ambassador, organizarás y liderarás Book Clubs o Silent Readings, facilitando las conversaciones y creando experiencias enriquecedoras en entornos únicos.`,

    BAmbassText: `¿Por qué convertirse en Ambassador?`,
    ShareText: `Comparte tu pasión:`,
    ShareInfotext: `     Usa tu amor por la lectura para reunir a las personas y marcar la diferencia.`,
    EarnText: `Gana recompensas:`,
    EarnInfoText: ` Únete a nuestro programa de fidelidad, desbloquea nuevos niveles según tu compromiso y puntos acumulados, y gana recompensas.`,
    BuildText: `Construye tu comunidad:`,
    BuildInfoText: ` Conoce a otros amantes de los libros y crea amistades duraderas.`,
    LastBText: `Si te encantan los libros y te gusta reunir a las personas, comienza hoy tu viaje como Ambassador: ¡esta es tu oportunidad para brillar!`,
    BAmbssdBtnText: `Conviértete en Ambassador`,

    FRModel: `¿Listo para convertirte en un Ambassador de Ofellio?`,
    SModel: `Comienza el proceso respondiendo a unas pocas preguntas sencillas.`,
    SBtn: `Comenzar`,

    BAmbssdAn: `Conviértete en Ambassador`,
    AvrgText: `Tu número promedio de libros leídos en un año es`,
    UpToMoreText: ["hasta 3", "hasta 6", "hasta 12", "Más de 12"],

    YourText: `Eres un/una`,
    YourArray: ["Estudiante", "Trabajador", "Jubilado", "Otro"],

    YourFavrText: `Tus géneros favoritos son`,
    TypeGerne: `Escribe género`,

    LedText: `¿Has dirigido algún book club en el pasado?`,
    YesNoText: ["Si", "No"],

    DiscussText: `Explica en pocas líneas cómo organizarías un evento de book club (por ejemplo, ubicación, cómo guiarías la discusión, etc.).`,

    WishText: `Explica en pocas líneas por qué deseas convertirte en un Ofellio Ambassador.`,
    SubmitBtn: `Enviar`,

    LTModel: `¡Gracias por enviar tus respuestas!`,
    ReciveText: ` Pronto recibirás un correo electrónico de confirmación con las directrices del Ambassador.`,
  },
  francais: {
    QAmbassText: `Qu'est-ce qu'un Ambassador ?`,
    AAmbassText: `Un Ambassador est un organisateur et un guide, inspirant les autres à se retrouver autour des livres. En tant qu'Ambassador, vous organiserez et animerez des Book Clubs ou des Silent Readings, en facilitant les échanges et en créant des expériences uniques et mémorables`,

    BAmbassText: `Pourquoi devenir Ambassador?`,
    ShareText: `Partagez votre passion:`,
    ShareInfotext: `     Utilisez votre amour de la lecture pour rassembler les gens et faire une différence.`,
    EarnText: `Gagnez des récompenses:`,
    EarnInfoText: ` Rejoignez notre programme de fidélité, débloquez de nouveaux niveaux en fonction de votre engagement et des points accumulés, et gagnez des récompenses.`,
    BuildText: `Construisez votre communauté:`,
    BuildInfoText: ` Rencontrez d'autres passionnés de livres et créez des amitiés durables.`,
    LastBText: `Si vous aimez les livres et aimez rassembler les gens, commencez votre parcours en tant qu'Ambassador dès aujourd'hui : c'est votre opportunité de briller !`,
    BAmbssdBtnText: `Devenez Ambassador`,

    FRModel: `Prêt à devenir un Ambassador Ofellio ?`,
    SModel: `     Commencez le processus en répondant à quelques questions simples !`,
    SBtn: `Commencer`,

    BAmbssdAn: `Devenez un Ambassador`,
    AvrgText: `Votre nombre moyen de livres lus par an est`,
    UpToMoreText: ["jusqu'à 3", "jusqu'à 6", "jusqu'à 12", "Plus de 12"],

    YourText: `Tu es un/une`,
    YourArray: ["Étudian", "Travailleur", "Retraité", "Autre"],

    YourFavrText: `Tes genres préférés sont`,
    TypeGerne: `Tapez le genre`,

    LedText: `As-tu dirigé des book clubs dans le passé ?`,
    YesNoText: ["Oui", "Non"],

    DiscussText: `Explique en quelques lignes comment tu organiserais un événement de book club (par exemple, lieu, comment guider la discussion, etc.)`,

    WishText: `Explique en quelques lignes pourquoi tu souhaites devenir un Ofellio Ambassador.`,
    SubmitBtn: `Envoyer`,

    LTModel: `Merci d'avoir soumis vos réponses !`,
    ReciveText: `Vous recevrez bientôt un e-mail de confirmation avec les directives de l'Ambassador.`,
  },
  nihongo: {
    QAmbassText: `「Ambassadorとは？」`,
    AAmbassText: `Ambassadorは、イベントの企画者であり、ガイドとして、本を通じたつながりを生み出します。Ambassadorとして、Book ClubやSilent Readingを企画・運営し、会話を促し、特別で心に残る体験を提供します。`,

    BAmbassText: `「なぜAmbassadorになるのか？」`,
    ShareText: `情熱を共有しましょう：`,
    ShareInfotext: `     読書への愛を活かして、人々をつなげ、違いを生み出しましょう。`,
    EarnText: `報酬を獲得しましょう：`,
    EarnInfoText: ` 私たちのロイヤルティプログラムに参加し、あなたの活動と獲得したポイントに応じて新しいレベルを解放し、報酬を得ましょう。`,
    BuildText: `コミュニティを築きましょう：`,
    BuildInfoText: ` 他の読書愛好者と出会い、長続きする友情を築きましょう。`,
    LastBText: `本が大好きで、人々をつなげるのが得意な方は、今すぐAmbassadorとしての旅を始めましょう：これがあなたの輝くチャンスです！`,
    BAmbssdBtnText: `Ambassadorになる`,

    FRModel: `OfellioのAmbassadorになる準備はできましたか？`,
    SModel: `        いくつかの簡単な質問に答えて、プロセスを始めましょう！`,
    SBtn: `開始`,

    BAmbssdAn: `Ambassador〜になる`,
    AvrgText: `あなたの年間平均読書冊数は`,
    UpToMoreText: ["最大で 3", "最大で 6", "最大で 12", "より多い 12"],

    YourText: `あなたは〜です`,
    YourArray: ["学生", "働いている人", "引退した", "その他"],

    YourFavrText: `あなたの好きなジャンルは`,
    TypeGerne: `ジャンルを入力`,

    LedText: `過去にbook clubsを運営したことがありますか？`,
    YesNoText: ["はい", "いいえ"],

    DiscussText: `どのようにbook clubのイベントを組織するかを簡潔に説明してください（例：場所、議論の進行方法など`,

    WishText: `なぜOfellio Ambassadorになりたいのかを簡潔に説明してください`,
    SubmitBtn: `送る`,

    LTModel: `回答を提出していただきありがとうございます！`,
    ReciveText: `まもなく、Ambassadorのガイドラインを記載した確認メールをお送りします。`,
  },
  nederlands: {
    QAmbassText: `Wat is een Ambassadeur?`,
    AAmbassText: `Een Ambassadeur is een coördinator, een gids en een gemeenschapsbouwer die anderen inspireert om via boeken te verbinden. Als Ambassadeur organiseer en leid je Boekenclubs of Stille Leessessies, leid je discussies en creëer je memorabele ervaringen in unieke settings.`,
    BAmbassText: `Waarom Ambassadeur worden?`,
    ShareText: `Deel je passie:`,
    ShareInfotext: `Gebruik je liefde voor lezen om mensen samen te brengen en een verschil te maken.`,
    EarnText: `Verdien beloningen:`,
    EarnInfoText: `Sluit je aan bij ons loyaliteitsprogramma, ontgrendel nieuwe niveaus op basis van je betrokkenheid en verzamelde punten, en verdien beloningen.`,
    BuildText: `Bouw je gemeenschap:`,
    BuildInfoText: `Ontmoet mede-boekliefhebbers en creëer blijvende vriendschappen.`,
    LastBText: `Als je van boeken houdt en geniet van het samenbrengen van mensen, begin vandaag je reis als Ambassadeur: dit is jouw kans om te stralen!`,
    BAmbssdBtnText: `Word Ambassadeur`,
    FRModel: `Klaar om een Ofellio Ambassadeur te worden?`,
    SModel: `Start het proces door een paar eenvoudige vragen te beantwoorden!`,
    SBtn: `Start`,
    BAmbssdAn: `Word Een Ambassadeur`,
    AvrgText: `Je gemiddelde aantal gelezen boeken per jaar is`,
    UpToMoreText: ["Tot 3", "Tot 6", "Tot 12", "Meer dan 12"],
    YourText: `Je bent een `,
    YourArray: ["Student", "Werknemer", "Gepensioneerde", "Anders"],
    YourFavrText: `Je favoriete genres zijn `,
    TypeGerne: `Type genre`,
    LedText: `Heb je in het verleden boekclubs geleid?`,
    YesNoText: ["Ja", "Nee"],
    DiscussText: `Leg in een paar regels uit hoe je een boekclub evenement zou organiseren (bijv. locatie, hoe je de discussie zou begeleiden, etc.)`,
    WishText: `Leg in een paar regels uit waarom je een Ofellio Ambassadeur wilt worden`,
    SubmitBtn: `Verzenden`,
    LTModel: `Bedankt voor het indienen van je antwoorden!`,
    ReciveText: `Je ontvangt binnenkort een bevestigings-e-mail met de richtlijnen voor Ambassadeurs.`,
  },
  chinese: {
    QAmbassText: `什么是大使？`,
    AAmbassText: `大使是协调员、向导和社区建设者，激励他人通过图书建立联系。作为大使，您将组织和领导图书俱乐部或静读会，引导讨论并在独特的环境中创造难忘的体验。`,
    BAmbassText: `为什么要成为大使？`,
    ShareText: `分享您的热情：`,
    ShareInfotext: `利用您对阅读的热爱将人们聚集在一起并产生影响。`,
    EarnText: `获得奖励：`,
    EarnInfoText: `加入我们的忠诚度计划，根据您的参与度和收集的积分解锁新级别，并获得奖励。`,
    BuildText: `建立您的社区：`,
    BuildInfoText: `结识其他图书爱好者并建立持久的友谊。`,
    LastBText: `如果您热爱图书并喜欢将人们聚集在一起，今天就开始您作为大使的旅程：这是您发光的机会！`,
    BAmbssdBtnText: `成为大使`,
    FRModel: `准备好成为Ofellio大使了吗？`,
    SModel: `通过回答几个简单的问题开始这个过程！`,
    SBtn: `开始`,
    BAmbssdAn: `成为大使`,
    AvrgText: `您一年中平均阅读的图书数量是`,
    UpToMoreText: ["最多3本", "最多6本", "最多12本", "超过12本"],
    YourText: `您是 `,
    YourArray: ["学生", "工作者", "退休人员", "其他"],
    YourFavrText: `您喜欢的类型是 `,
    TypeGerne: `输入类型`,
    LedText: `您过去是否领导过图书俱乐部？`,
    YesNoText: ["是", "否"],
    DiscussText: `用几句话解释您将如何组织图书俱乐部活动（例如：地点、如何引导讨论等）`,
    WishText: `用几句话解释您为什么想成为Ofellio大使`,
    SubmitBtn: `提交`,
    LTModel: `感谢您提交答案！`,
    ReciveText: `您将很快收到一封确认电子邮件，其中包含大使指南。`,
  },
};
const Ambassadors = () => {
  const { selectedLanguage } = useContext(LanguageContext);
  const copy =
    AmbassadorAll[selectedLanguage] ?? AmbassadorAll["english"];
  const { user, setShowLoginM } = ChatState();
  const navigate = useNavigate();
  // State to track the selected box
  const [selectedBox, setSelectedBox] = useState(null);
  const [selectedBoxQ2, setSelectedBoxQ2] = useState(null);
  const [selectedBoxQ3, setSelectedBoxQ3] = useState(null);
  const [selectedBoxQ4, setSelectedBoxQ4] = useState(null);
  const [discussionInfo, setDiscussionInfo] = useState(""); // ✅ Store textarea input
  const [wishesInfo, setWishesInfo] = useState(""); // ✅ Store textarea input

  // const [selectedBoxQ5, setSelectedBoxQ5] = useState(null);

  // Function to handle box selection
  const handleBoxClick = (boxIndex) => {
    setSelectedBox(boxIndex);
    setQ2Show(true);
    setQShow(false);
  };

  // Function to handle box selection
  const handleBoxClickQ2 = (boxIndexQ2) => {
    setSelectedBoxQ2(boxIndexQ2);
    setQ2Show(false);
    setQ3Show(true);
  };

  // Function to handle box selection
  const handleBoxClickQ3 = (boxIndexQ3) => {
    setSelectedBoxQ3(boxIndexQ3);
    setQ3Show(false);
    setQ4Show(true);

    // setSearchQuery(genres[boxIndexQ3]); // Update the input value
  };

  // Function to handle box selection
  const handleBoxClickQ4 = (boxIndexQ4) => {
    setSelectedBoxQ4(boxIndexQ4);
    setQ4Show(false);
    setQ5Show(true);
  };

  // const handleBoxClickQ5 = (boxIndexQ5) => {
  //   setSelectedBoxQ5(boxIndexQ5);
  // };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        //setFilteredGenres([]); // Close the dropdown
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const [show, setShow] = useState(false); // For signup modal
  const [qShow, setQShow] = useState(false); // For signup modal
  const [q2Show, setQ2Show] = useState(false); // For signup modal
  const [q3Show, setQ3Show] = useState(false); // For signup modal
  const [q4Show, setQ4Show] = useState(false); // For signup modal
  const [q5Show, setQ5Show] = useState(false); // For signup modal
  const [q6Show, setQ6Show] = useState(false); // For signup modal
  const [q7Show, setQ7Show] = useState(false); // For signup modal

  const location = useLocation();

  // Check location state to open modal if triggered
  useEffect(() => {
    if (user?.isAmbassador) {
      toast.error("You're already Ambassador!");
      return;
    }
    const isModalClosed = localStorage.getItem("modalClosedA");
    if (location.state?.show && !isModalClosed) {
      setOpen(true);
    }
  }, [location.state]);
  // Handle closing both modals
  const handleClose = () => {
    setShow(false); // Close signup modal
    localStorage.setItem("modalClosedA", "true");
  };

  const handleQClose = () => {
    setQShow(false); // Close signup modal
    setShow(false);
    setQ2Show(false);
  };

  const handleQ2Close = () => {
    setQ2Show(false); // Close signup modal
    setQShow(false);
    setShow(false);
  };

  const handleQ3Close = () => {
    setQ3Show(false); // Close signup modal

    setQ2Show(false); // Close signup modal
    setShow(false);
    setQShow(false);
  };

  const handleQ4Close = () => {
    setQ4Show(false); // Close signup modal

    setQ3Show(false); // Close signup modal

    setQ2Show(false); // Close signup modal
    setShow(false);
    setQShow(false);
  };

  const handleQ5Close = () => {
    setQ5Show(false);
    setQ4Show(false); // Close signup modal

    setQ3Show(false); // Close signup modal

    setQ2Show(false); // Close signup modal
    setQShow(false);
    setShow(false);
  };

  const handleQ6Close = () => {
    setQ6Show(false);

    setQ5Show(false);
    setQ4Show(false); // Close signup modal

    setQ3Show(false); // Close signup modal

    setQ2Show(false); // Close signup modal
    setQShow(false);
    setShow(false);
  };

  const handleQ7Close = () => {
    setQ7Show(false);
    setQ6Show(false);
    setQ5Show(false);
    setQ4Show(false); // Close signup modal

    setQ3Show(false); // Close signup modal

    setQ2Show(false); // Close signup modal
    setQShow(false);
    setShow(false);
  };

  // Handle opening Show modal

  const handleShow = () => {
    setShow(true); // Open signup modal
  };

  const handleQShow = async () => {
    try {
      const res = await axios.get(
        `/api/v1/auth/check-ambassador/${auth?.user?._id}`
      );

      const { applied, isAmbassador } = res.data;

      if (isAmbassador) {
        toast.error("You are Already an Ambassador.");
      } else if (applied) {
        toast.error("You have already applied for an ambassador account.");
      } else {
        // Open apply form modal
        setQShow(true);
        setShow(false);
        setQ2Show(false);
        setQ3Show(false);
        setQ4Show(false);
        setQ5Show(false);
        setQ6Show(false);
        setQ7Show(false);
      }
    } catch (error) {
      console.log("Error checking ambassador status", error);
      toast.error("Something went wrong.");
    }
  };

  const handleQ2Show = () => {
    setQ2Show(true); // Open signup modal
    setQShow(false);
    setQ6Show(false);
    setQ5Show(false);
    setQ4Show(false);
    setQ3Show(false);
    setQ7Show(false);

    setShow(false);
  };

  const handleQ3Show = () => {
    setQ3Show(true);
    setQ2Show(false); // Open signup modal
    setQShow(false);
    setQ4Show(false);
    setQ5Show(false);
    setQ6Show(false);
    setQ7Show(false);

    setShow(false);
  };

  const handleQ4Show = () => {
    setQ4Show(true);
    setQ5Show(false);

    setQ3Show(false);
    setQ6Show(false);
    setQ7Show(false);

    setQ2Show(false); // Open signup modal
    setQShow(false);
    setShow(false);
  };

  const handleQ5Show = () => {
    setQ5Show(true);
    setQ7Show(false);

    setQ4Show(false);
    setQ3Show(false);
    setQ2Show(false); // Open signup modal
    setQShow(false);
    setShow(false);
  };
  const handleQ6Show = () => {
    setQ6Show(true);
    setQ7Show(false);
    setQ5Show(false);

    setQ4Show(false);
    setQ3Show(false);
    setQ2Show(false); // Open signup modal
    setQShow(false);
    setShow(false);
  };

  const handleQ7Show = () => {
    setQ7Show(true);
    setQ6Show(false);
    setQ5Show(false);

    setQ4Show(false);
    setQ3Show(false);
    setQ2Show(false); // Open signup modal
    setQShow(false);
    setShow(false);
  };
  // gobackhandle

  const handleGoBack = () => {
    setQShow(false);
    setQ2Show(false);
    setQ3Show(false);
    setQ4Show(false);
    setQ5Show(false);
    setQ6Show(false);
  };

  const handleGoBackQ2 = () => {
    setQShow(true);
    setQ2Show(false);
  };
  const handleGoBackQ3 = () => {
    setQShow(false);
    setQ3Show(false);
    setQ2Show(true);
  };

  const handleGoBackQ4 = () => {
    setQShow(false);
    setQ2Show(false);
    setQ4Show(false);
    setQ3Show(true);
  };

  const handleGoBackQ5 = () => {
    setQShow(false);
    setQ2Show(false);
    setQ3Show(false);
    setQ5Show(false);
    setQ4Show(true);
  };

  const handleGoBackQ6 = () => {
    setQShow(false);
    setQ2Show(false);
    setQ3Show(false);
    setQ4Show(false);
    setQ6Show(false);
    setQ5Show(true);
  };

  const handleGoNextQ6 = () => {
    setQ6Show(true);
    setQ5Show(false);
  };
  const handleGoNextQ3 = () => {
    if (selectedGenres.length > 0) {
      setQ3Show(false);
      setQ4Show(true);
    } else {
      toast.error("Please select first genre");
    }
  };

  const [imageLoaded, setImageLoaded] = useState(false);

  useEffect(() => {
    // Preload image
    const img = new Image();
    img.src = require("./image/ambasdormodelc.jpg");
    img.onload = () => setImageLoaded(true); // Mark as loaded
  }, []);

  // AMbassador intgrationn

  const [auth] = useAuth(); // ✅ Get auth from AuthContext
  const { setLoading } = useApp(); // ✅ Get setLoading from AppContext

  // AMbassador intgrationn

  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const total = 7; // last step is success

  // data
  const [avgRead, setAvgRead] = useState("");
  const [role, setRole] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedGenres, setSelectedGenres] = useState([]);
  const [ledClubs, setLedClubs] = useState(null);
  const [plan, setPlan] = useState("");
  const [motivation, setMotivation] = useState("");

  const containerRef = useRef(null);

  const allGenres = [
    "Crime",
    "Fiction",
    "Graphic Novels & Manga",
    "Young Adults & LGBTQ+",
    "Non-Fiction",
    "Sci-Fi & Fantasy",
  ];

  const filteredGenres = useMemo(() => {
    if (!searchQuery) return [];
    return allGenres.filter(
      (g) =>
        g.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !selectedGenres.includes(g)
    );
  }, [searchQuery, selectedGenres]);

  const handleSearchChange = (e) => setSearchQuery(e.target.value);

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const newGenre = searchQuery.trim();
      if (newGenre && !selectedGenres.includes(newGenre)) {
        setSelectedGenres([...selectedGenres, newGenre]);
      }
      setSearchQuery("");
    }
  };

  const handleGenreClick = (genre) => {
    if (!selectedGenres.includes(genre)) {
      setSelectedGenres([...selectedGenres, genre]);
    }
    setSearchQuery("");
  };

  const handleAddCustomClick = () => {
    const newGenre = searchQuery.trim();
    if (newGenre && !selectedGenres.includes(newGenre)) {
      setSelectedGenres([...selectedGenres, newGenre]);
    }
    setSearchQuery("");
  };

  const removeGenre = (genre) =>
    setSelectedGenres(selectedGenres.filter((g) => g !== genre));

  const next = () => setStep((s) => Math.min(s + 1, total));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const canNext = useMemo(() => {
    if (step === 0) return true;
    if (step === 1) return !!avgRead;
    if (step === 2) return !!role;
    if (step === 3) return selectedGenres.length > 0;
    if (step === 4) return ledClubs !== null;
    if (step === 5) return plan.trim().length > 15;
    if (step === 6) return motivation.trim().length > 15;
    return true;
  }, [step, avgRead, role, selectedGenres, ledClubs, plan, motivation]);
  const handleFinish = async () => {
    if (!auth?.user?._id) {
      message.error("Please login first");
      return;
    }
    const bookAverageMap = {
      "Up to 3": 0,
      "Up to 6": 1,
      "Up to 12": 2,
      "More than 12": 3,
    };

    const studentMap = {
      Student: 0,
      Worker: 1,
      Retired: 2,
      Other: 3,
    };

    const pastMap = {
      Yes: 1,
      No: 0,
    };
    try {
      setLoading(true);

      const res = await axios.post(
        "api/v1/auth/apply-ambassador",
        {
          userId: auth?.user?._id,
          bookavargae: bookAverageMap[avgRead] ?? 0, // ✅ default 0 if not found
          student: studentMap[role] ?? 0,
          past: pastMap[ledClubs] ?? 0,
          genres: selectedGenres, // ✅ Already array of strings
          discussion: plan,
          wishes: motivation,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      setLoading(false);

      if (res.data.success) {
        message.success(res.data.message);
        setStep(7);
      } else {
        message.error(res.data.message);
      }
    } catch (error) {
      setLoading(false);
      if (error.response?.data?.message) {
        message.error(error.response.data.message);
      } else {
        message.error("Something went wrong. Please try again.");
      }
    }
  };

  return (
    <>
      <div className="min-h-screen bg-white text-gray-800">
        {qShow || q2Show || q3Show || q4Show || q5Show || q6Show || q7Show ? (
          <div className="d-none">
            <Header3 />
          </div>
        ) : (
          <Header3 />
        )}
        {/* Hero Section */}
        <section className="relative bg-[#34c3c9] text-white py-14 sm:py-20 px-4 sm:px-6 text-center overflow-hidden">
          {/* Decorative background pattern */}
          <div className="absolute inset-0 opacity-20 bg-[#34c3c9]" />
          <div className="absolute inset-0 opacity-10 bg-[#34c3c9] bg-repeat" />

          <motion.h1
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="relative text-3xl sm:text-4xl font-bold mb-3 sm:mb-4 z-10 leading-tight"
          >
            {copy?.BAmbssdAn || "Become an Ambassador"}
          </motion.h1>
          <p className="relative mx-auto max-w-xl sm:max-w-2xl text-base sm:text-lg z-10">
            {copy?.AAmbassText ||
              "Share your love of reading, connect with others, and unlock rewards by becoming a part of our growing community."}
          </p>
        </section>

        {/* Content Section */}
        <section className="mx-auto max-w-5xl py-12 sm:py-16 px-4 sm:px-6 grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-10">
          <Card className="rounded-2xl shadow-lg hover:shadow-xl transition-shadow">
            <CardContent className="p-6 sm:p-8 flex flex-col items-center text-center">
              <Megaphone
                size={44}
                className="text-[#34c3c9] mb-[12px] sm:mb-[16px"
              />
              <h2 className="text-xl sm:text-2xl font-semibold mb-[8px] sm:mb-[12px] text-[#34c3c9]">
                {copy?.QAmbassText || "What is an Ambassador?"}
              </h2>
              <p className="text-sm sm:text-base leading-6 sm:leading-7">
                {copy?.AAmbassText ||
                  "An Ambassador is a coordinator, guide, and community builder, inspiring others to connect over books. You will organize and lead Book Clubs or Silent Readings, creating memorable experiences in unique settings."}
              </p>
            </CardContent>
          </Card>

          <Card className="rounded-2xl shadow-lg hover:shadow-xl transition-shadow bg-[#fff]">
            <CardContent className="p-6 sm:p-8 flex flex-col items-center text-center">
              <BookOpen size={44} className="text-[#34c3c9] mb-3 sm:mb-4" />
              <h2 className="text-xl sm:text-2xl font-semibold mb-2 sm:mb-3 text-[#34c3c9]">
                {copy?.BAmbassText || "Why become an Ambassador?"}
              </h2>
              <ul className="space-y-2 sm:space-y-3 text-left text-sm sm:text-base leading-6 sm:leading-7">
                <li>
                  <strong>
                    {copy?.ShareText || "Share your passion:"}
                  </strong>{" "}
                  {copy?.ShareInfotext ||
                    "Use your love of reading to bring people together."}
                </li>
                <li>
                  <strong>{copy?.EarnText || "Earn rewards:"}</strong>{" "}
                  {copy?.EarnInfoText ||
                    "Join our loyalty program and unlock exclusive perks."}
                </li>
                <li>
                  <strong>
                    {copy?.BuildText || "Build your community:"}
                  </strong>{" "}
                  {copy?.BuildInfoText ||
                    "Meet fellow book enthusiasts and form lasting friendships."}
                </li>
              </ul>
            </CardContent>
          </Card>
        </section>

        {/* CTA Section */}
        <section className="bg-[#f2f2f2] py-12 sm:py-16 px-4 sm:px-6 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold mb-3 sm:mb-4 text-[#34c3c9]">
            {copy?.FRModel || "Ready to start your journey?"}
          </h2>
          <p className="mx-auto max-w-xl sm:max-w-2xl mb-5 sm:mb-6 text-sm sm:text-base">
            {copy?.LastBText ||
              "If you love books and enjoy bringing people together, this is your opportunity to shine."}
          </p>
          <div className="mx-auto max-w-md">
            <Button
              size="lg"
              className="w-full font-bold sm:w-auto bg-[#34c3c9] text-white  rounded-[16px] px-[32px] py-[12px] shadow-md hover:bg-[#2aa5ab]"
              onClick={() => {
                if (!user) {
                  setShowLoginM(true);
                  return;
                }
                if (user?.isAmbassador) {
                  toast.error("You're already Ambassador!");
                  return;
                }
                setOpen(true);
              }}
            >
              {copy?.BAmbssdBtnText || "Become an Ambassador"}
            </Button>
          </div>
        </section>
        <div
          className={
            qShow || q2Show || q3Show || q4Show || q5Show || q6Show || q7Show
              ? "  gapmrtaamod "
              : "gapmrtaa gapmrtaa1 gspzero"
          }
        >
          {show ||
          qShow ||
          q2Show ||
          q3Show ||
          q4Show ||
          q5Show ||
          q6Show ||
          q7Show ? (
            <Footer3 />
          ) : (
            <Footer2 />
          )}
        </div>
      </div>

      {/* Popup Modal */}
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="relative w-full max-w-3xl rounded-3xl border border-slate-100 bg-white/95 p-6 shadow-xl backdrop-blur sm:p-8">
            <button
              onClick={() => setOpen(false)}
              className="absolute right-4 top-4 rounded-full p-2 text-slate-500 hover:bg-slate-100"
            >
              <X size={20} />
            </button>

            {/* header */}
            <div className="mb-4 text-center">
              <div
                className="mx-auto mb-2 inline-flex items-center gap-2 rounded-full bg-[var(--p)]/10 px-3 py-1 text-xs font-semibold text-[var(--p)]"
                style={{ ["--p"]: PRIMARY }}
              >
              <Sparkles size={14} />{" "}
              {copy?.BAmbssdAn || "Become an Ambassador"}
              </div>
              <Title>{copy?.FRModel || "Ready to become an Ofellio Ambassador?"}</Title>
              <Sub>{copy?.SModel || "Start the process by answering a few simple questions!"}</Sub>
            </div>

            <Progress step={Math.min(step, total - 1)} total={total - 1} />

            {/* card area */}
            <div className="relative mx-auto mt-2 grid min-h-[340px] place-items-center rounded-2xl bg-white p-5 ring-1 ring-slate-200 sm:p-8">
              {step === 0 && (
                <div className="text-center">
                  <Title>
                    {copy?.FRModel || "Ready to become an Ofellio Ambassador?"}
                  </Title>
                  <Sub>
                    {copy?.SModel ||
                      "Start the process by answering a few simple questions."}
                  </Sub>
                  <div className="mt-6">
                    <CTA onClick={next}>{copy?.SBtn || "Start"}</CTA>
                  </div>
                </div>
              )}

              {step === 1 && (
                <div className="w-full">
                  <Title>
                    {copy?.AvrgText ||
                      "Your average number of books read in a year is"}
                  </Title>
                  <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {(copy?.UpToMoreText ||
                      ["Up to 3", "Up to 6", "Up to 12", "More than 12"]).map(
                      (opt) => (
                        <SegButton
                          key={opt}
                          active={avgRead === opt}
                          onClick={() => setAvgRead(opt)}
                        >
                          {opt}
                        </SegButton>
                      )
                    )}
                  </div>
                </div>
              )}

              {step === 2 && (
                <div className="w-full">
                  <Title>{copy?.YourText || "You are a"}</Title>
                  <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {(copy?.YourArray || [
                      "Student",
                      "Worker",
                      "Retired",
                      "Other",
                    ]).map((opt) => (
                      <SegButton
                        key={opt}
                        active={role === opt}
                        onClick={() => setRole(opt)}
                      >
                        {opt}
                      </SegButton>
                    ))}
                  </div>
                </div>
              )}

              {step === 3 && (
                <div className="w-full max-w-xl">
                  <Title>
                    {copy?.YourFavrText || "Your favorite genres are"}
                  </Title>
                  <div className="mt-5 flex justify-center">
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={handleSearchChange}
                      onKeyDown={handleKeyDown}
                      placeholder={copy?.TypeGerne || "Type genre"}
                      className="form-control w-full max-w-xs rounded-xl border border-black px-3 py-2 text-sm outline-none"
                      style={{ color: "#787878" }}
                    />
                  </div>

                  <div
                    ref={containerRef}
                    className="relative mt-2 flex flex-col items-center"
                  >
                    {(filteredGenres.length > 0 || searchQuery.trim()) && (
                      <ul
                        className="absolute z-10 w-1/2 transform -translate-x-1/2 list-none rounded-md border border-gray-300 bg-white text-center shadow-md"
                        style={{ left: "50%" }}
                      >
                        {filteredGenres.map((genre, index) => (
                          <li
                            key={index}
                            className="cursor-pointer border-b px-4 py-2 hover:bg-gray-100"
                            onClick={() => handleGenreClick(genre)}
                          >
                            {genre}
                          </li>
                        ))}
                        {searchQuery.trim() &&
                          !allGenres.includes(searchQuery.trim()) && (
                            <li
                              className="cursor-pointer px-4 py-2 text-blue-600 hover:bg-blue-50"
                              onClick={handleAddCustomClick}
                            >
                              + Add "{searchQuery.trim()}"
                            </li>
                          )}
                      </ul>
                    )}

                    {selectedGenres.length > 0 && (
                      <div className="mt-4 flex w-full max-w-sm flex-wrap justify-center gap-2">
                        {selectedGenres.map((genre, index) => (
                          <div
                            key={index}
                            className="relative rounded-md border-2 border-black bg-[#62C0C7] px-4 py-1 text-white"
                          >
                            {genre}
                            <span
                              className="absolute -right-2 -top-2 cursor-pointer rounded-full border border-gray-300 bg-white px-1 text-xs text-black"
                              onClick={() => removeGenre(genre)}
                            >
                              ✕
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {step === 4 && (
                <div className="w-full">
                  <Title>
                    {copy?.LedText || "Have you led any book clubs in the past?"}
                  </Title>
                  <div className="mt-6 grid grid-cols-2 gap-3">
                    {(copy?.YesNoText || ["Yes", "No"]).map((opt, idx) => (
                      <SegButton
                        key={opt}
                        active={ledClubs === (idx === 0)}
                        onClick={() => setLedClubs(idx === 0)}
                      >
                        {opt}
                      </SegButton>
                    ))}
                  </div>
                </div>
              )}

              {step === 5 && (
                <div className="w-full">
                  <Title>
                    {copy?.DiscussText ||
                      "Explain briefly how you would organize a book club event"}
                  </Title>
                  <textarea
                    value={plan}
                    onChange={(e) => setPlan(e.target.value)}
                    placeholder={
                      copy?.DiscussText ||
                      "Location, structure, discussion prompts, timing, etc."
                    }
                    className="mt-4 h-44 w-full rounded-xl border border-slate-300 p-3 text-sm outline-none focus:ring-2 focus:ring-[var(--p)]"
                    style={{ ["--p"]: PRIMARY }}
                  />
                  <p className="mt-1 text-right text-xs text-slate-500">
                    {Math.max(0, 16 - plan.trim().length)} chars to go
                  </p>
                </div>
              )}

              {step === 6 && (
                <div className="w-full">
                  <Title>
                    {copy?.WishText ||
                      "Why do you want to become an Ofellio Ambassador?"}
                  </Title>
                  <textarea
                    value={motivation}
                    onChange={(e) => setMotivation(e.target.value)}
                    placeholder={
                      copy?.WishText ||
                      "Share what motivates you to bring people together through books."
                    }
                    className="mt-4 h-44 w-full rounded-xl border border-slate-300 p-3 text-sm outline-none focus:ring-2 focus:ring-[var(--p)]"
                    style={{ ["--p"]: PRIMARY }}
                  />
                </div>
              )}

              {step === 7 && (
                <div className="text-center">
                  <div
                    className="mx-auto mb-4 grid h-14 w-14 place-items-center rounded-full bg-[var(--p)]/10 text-[var(--p)]"
                    style={{ ["--p"]: PRIMARY }}
                  >
                    <Check />
                  </div>
                  <Title>
                    {copy?.LTModel ||
                      "Thank you for submitting your answers!"}
                  </Title>
                  <Sub>
                    {copy?.ReciveText ||
                      "You will receive a confirmation email soon with the Ambassador guidelines."}
                  </Sub>
                </div>
              )}
            </div>

            {/* Nav */}
            {step > 0 && step < 7 && (
              <div className="mt-6 flex items-center justify-between">
                <Ghost onClick={back}>
                  <ChevronLeft size={18} /> Back
                </Ghost>
                {step < 6 && (
                  <CTA
                    onClick={next}
                    disabled={!canNext}
                    className={!canNext ? "opacity-60 cursor-not-allowed" : ""}
                  >
                    Next <ChevronRight size={18} />
                  </CTA>
                )}
                {step === 6 && (
                  <CTA onClick={(e) => handleFinish(e)} className={""}>
                    Submit
                  </CTA>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      <Modal
        show={show}
        onHide={handleClose}
        centered
        className="custom-modalmeet"
        backdrop={true}
        onShow={() => {
          setTimeout(() => {
            const img = document.querySelector(".ambasdormodel");
            if (img) {
              img.style.visibility = "visible"; // Only update style if element exists
            }
          }, 0);
        }}
      >
        <Modal.Header className=" border-0"></Modal.Header>
        <Modal.Title className="text-center mt-3 ">
          {imageLoaded ? (
            <img
              src={require("./image/ambasdormodelc.jpg")}
              className="ambasdormodel mx-auto"
              width="107"
              height="74"
              // style={{ visibility: "hidden" }} // Hide initially
            />
          ) : (
            <div style={{ width: "107px", height: "74px" }} />
          )}
        </Modal.Title>

        <Modal.Body className="mx-auto">
          <div
            className="fs-4 fw-bold  text-center w-100 ftzcopy mt-4  "
            style={{ maxWidth: "677px" }}
          >
            {/* Ready to become an Ofellio Ambassador? */}
            {AmbassadorAll[selectedLanguage]?.FRModel}
          </div>

          <div
            className="fs-4 fw-bold  text-center w-100 ftzcopy mt-4  "
            style={{ maxWidth: "677px" }}
          >
            {/* Start the process answering a few simple questions! */}
            {AmbassadorAll[selectedLanguage]?.SModel}
          </div>

          <div
            className={`text-center ${
              ["francais", "español", "italiano", "deutsch"].includes(
                selectedLanguage
              )
                ? "mt-4"
                : "mt-5"
            }`}
          >
            <button
              className="w-100 border-0 fw600 text-white amsdorbtnmod rounded-1"
              style={{
                maxWidth: "190px",
                height: "52px",
                backgroundColor: "#34C3C9",
                fontSize: "30px",
              }}
              onClick={handleQShow} // Trigger modal on click
            >
              {/* Start */}
              {AmbassadorAll[selectedLanguage]?.SBtn}
            </button>
          </div>
        </Modal.Body>
      </Modal>

      <Modal
        show={qShow}
        onHide={handleQClose}
        centered
        className="custom-modalques p-0"
      >
        <div className="text-center mtminmodel ">
          <div className="fw-bold text-uppercase fs-4  text-white ftzinput">
            {/* Become an Ambassador */}
            {AmbassadorAll[selectedLanguage]?.BAmbssdAn}
          </div>
          <div
            className="w-100 d-flex mx-auto mawitline"
            style={{
              border: "2px solid #BFBFBF",
              height: "2px",
              marginTop: "25px",
              maxWidth: "420px",
            }}
          >
            <div
              className="d-flex z-4 gap-md-5 gap-sm-4 gap-3   justify-content-center"
              style={{ marginTop: "-12px" }}
            >
              <div
                className={`rounded-circle ${
                  qShow ? "bgorg" : "bg-white"
                } cirwidhe`}
                style={{ width: "22px", height: "22px", marginLeft: "-15px" }}
                // onClick={handleQShow} // Trigger modal on click
              ></div>

              <div
                className="bg-white rounded-circle cirwidhe"
                style={{ width: "22px", height: "22px" }}
                // onClick={handleQ2Show}
              ></div>

              <div
                className="bg-white rounded-circle cirwidhe"
                style={{ width: "22px", height: "22px" }}
                // onClick={handleQ3Show}
              ></div>

              <div
                className="bg-white rounded-circle cirwidhe"
                style={{ width: "22px", height: "22px" }}
              ></div>

              <div
                className="bg-white rounded-circle cirwidhe"
                style={{ width: "22px", height: "22px" }}
              ></div>

              <div
                className="bg-white rounded-circle cirwidhe"
                style={{ width: "22px", height: "22px" }}
              ></div>
              <div
                className="bg-white rounded-circle cirwidhe"
                style={{ width: "22px", height: "22px" }}
              ></div>
            </div>
          </div>
        </div>
        <Modal.Header className=" border-0"></Modal.Header>
        <Modal.Title className="text-center  " style={{ marginTop: "35px" }}>
          <div className="d-flex gap-4 justify-content-center mx-5">
            <img
              src={require("./image/arrowback.png")}
              className="mt-2   clasarowmdl  "
              width="20"
              height="24"
              onClick={handleGoBack}
              style={{
                cursor: "pointer",
                rotate: "90deg",
              }}
            />

            <div
              className="fw-bold w-100 ftzinput "
              style={{ maxWidth: "430px ", fontSize: "22px" }}
            >
              {/* Your average number of books read in a year is… */}
              {AmbassadorAll[selectedLanguage]?.AvrgText}
            </div>
          </div>
        </Modal.Title>

        <Modal.Body className="mx-auto mt-md-4 mt-sm-5 mt-3  ">
          <div
            className="d-flex w-100 gap-md-4 gap-2  flex-wrap justify-content-center"
            style={{ maxWidth: "390px" }}
          >
            {/* {["Up to 3", "Up to 6", "Up to 12", "More than 12"].map( */}
            {AmbassadorAll[selectedLanguage]?.UpToMoreText.map(
              (label, index) => (
                <div
                  key={index}
                  className="text-center wstyupto ftzcopy fw-bold fs-5 py-1 rounded-1"
                  style={{
                    width: "180px",
                    height: "45px",
                    border: "2px solid #000000",
                    backgroundColor:
                      selectedBox === index ? "#62C0C7" : "transparent", // Blue background when selected
                    color: selectedBox === index ? "#fff" : "#000", // White text when selected
                    cursor: "pointer", // Add cursor pointer for a better UX
                  }}
                  onClick={() => handleBoxClick(index)} // Handle box click
                >
                  {label}
                </div>
              )
            )}
          </div>
        </Modal.Body>
      </Modal>

      <Modal
        show={q2Show}
        onHide={handleQ2Close}
        centered
        className="custom-modalques"
      >
        <div className="text-center mtminmodel">
          <div className="fw-bold text-uppercase fs-4 text-white ftzinput">
            {/* Become an Ambassador */}
            {AmbassadorAll[selectedLanguage]?.BAmbssdAn}
          </div>

          <div
            className="w-100 d-flex mx-auto mawitline"
            style={{
              border: "2px solid #BFBFBF",
              height: "2px",
              marginTop: "25px",
              maxWidth: "420px",
            }}
          >
            <div
              className="d-flex z-4 gap-md-5 gap-sm-4 gap-3   justify-content-center"
              style={{ marginTop: "-12px" }}
            >
              <div
                className="bg-white rounded-circle cirwidhe"
                style={{ width: "22px", height: "22px", marginLeft: "-15px" }}
                // onClick={handleQShow} // Trigger modal on click
              ></div>

              <div
                className={`rounded-circle ${
                  q2Show ? "bgorg" : "bg-white"
                } cirwidhe`}
                style={{ width: "22px", height: "22px" }}
                // onClick={handleQ2Show}
              ></div>

              <div
                className="bg-white rounded-circle cirwidhe"
                style={{ width: "22px", height: "22px" }}
                // onClick={handleQ3Show}
              ></div>

              <div
                className="bg-white rounded-circle cirwidhe"
                style={{ width: "22px", height: "22px" }}
              ></div>

              <div
                className="bg-white rounded-circle cirwidhe"
                style={{ width: "22px", height: "22px" }}
              ></div>

              <div
                className="bg-white rounded-circle cirwidhe"
                style={{ width: "22px", height: "22px" }}
              ></div>
              <div
                className="bg-white rounded-circle cirwidhe"
                style={{ width: "22px", height: "22px" }}
              ></div>
            </div>
          </div>
        </div>
        <Modal.Header className=" border-0"></Modal.Header>
        <Modal.Title className="text-center  " style={{ marginTop: "35px" }}>
          <div className="d-flex gap-4 justify-content-center mx-5">
            <img
              src={require("./image/arrowback.png")}
              className="mt-2 clasarowmdl"
              width="20"
              height="24"
              onClick={handleGoBackQ2}
              style={{ rotate: "90deg", cursor: "pointer" }}
            />

            <div
              className="fw-bold w-100 ftzinput"
              style={{ maxWidth: "430px ", fontSize: "22px" }}
            >
              {/* You are a..... */}
              {AmbassadorAll[selectedLanguage]?.YourText}
            </div>
          </div>
        </Modal.Title>

        <Modal.Body className="mx-auto  mt-sm-5 mt-3  ">
          <div
            className="d-flex w-100 gap-md-4 gap-2  flex-wrap justify-content-center"
            style={{ maxWidth: "390px" }}
          >
            {/* {["Student", "Worker", "Retired", "Other"].map((label, index) => ( */}
            {AmbassadorAll[selectedLanguage]?.YourArray.map((label, index) => (
              <div
                key={index}
                className="text-center wstyupto ftzcopy fw-bold fs-5 py-1 rounded-1"
                style={{
                  width: "180px",
                  height: "45px",
                  border: "2px solid #000000",
                  backgroundColor:
                    selectedBoxQ2 === index ? "#62C0C7" : "transparent", // Blue background when selected
                  color: selectedBoxQ2 === index ? "#fff" : "#000", // White text when selected
                  cursor: "pointer", // Add cursor pointer for a better UX
                }}
                onClick={() => handleBoxClickQ2(index)} // Handle box click
              >
                {label}
              </div>
            ))}
          </div>
        </Modal.Body>
      </Modal>

      <Modal
        show={q3Show}
        onHide={handleQ3Close}
        centered
        className={`custom-modalques ${
          selectedGenres.length >= 3 ? "custmheight" : ""
        }`}
      >
        <div className="text-center mtminmodel">
          <div className="fw-bold text-uppercase fs-4 text-white ftzinput">
            {/* Become an Ambassador */}
            {AmbassadorAll[selectedLanguage]?.BAmbssdAn}
          </div>
          <div
            className="w-100 d-flex mx-auto mawitline"
            style={{
              border: "2px solid #BFBFBF",
              height: "2px",
              marginTop: "25px",
              maxWidth: "420px",
            }}
          >
            <div
              className="d-flex z-4 gap-md-5 gap-sm-4 gap-3   justify-content-center"
              style={{ marginTop: "-12px" }}
            >
              <div
                className="bg-white rounded-circle cirwidhe"
                style={{ width: "22px", height: "22px", marginLeft: "-15px" }}
                // onClick={handleQShow} // Trigger modal on click
              ></div>

              <div
                className="bg-white rounded-circle cirwidhe"
                style={{ width: "22px", height: "22px" }}
                // onClick={handleQ2Show}
              ></div>

              <div
                className={`rounded-circle ${
                  q3Show ? "bgorg" : "bg-white"
                } cirwidhe`}
                style={{ width: "22px", height: "22px" }}
                // onClick={handleQ3Show}
              ></div>

              <div
                className="bg-white rounded-circle cirwidhe"
                style={{ width: "22px", height: "22px" }}
                // onClick={handleQ4Show}
              ></div>

              <div
                className="bg-white rounded-circle cirwidhe"
                style={{ width: "22px", height: "22px" }}
              ></div>

              <div
                className="bg-white rounded-circle cirwidhe"
                style={{ width: "22px", height: "22px" }}
              ></div>
              <div
                className="bg-white rounded-circle cirwidhe"
                style={{ width: "22px", height: "22px" }}
              ></div>
            </div>
          </div>
        </div>
        <Modal.Header className=" border-0"></Modal.Header>
        <Modal.Title className="text-center  " style={{ marginTop: "35px" }}>
          <div className="d-flex gap-4 justify-content-center mx-5">
            <img
              src={require("./image/arrowback.png")}
              className="mt-2 clasarowmdl"
              width="20"
              height="24"
              onClick={handleGoBackQ3}
              style={{ rotate: "90deg", cursor: "pointer" }}
            />

            <div
              className="fw-bold w-100 ftzinput"
              style={{ maxWidth: "430px ", fontSize: "22px" }}
            >
              {/* Your favorite genres are{" "} */}
              {AmbassadorAll[selectedLanguage]?.YourFavrText}
            </div>
            {/* <div className="mtarowinptagQ3"> */}
            <img
              src={require("./image/arrowback.png")}
              className="mt-2 clasarowmdl"
              width="20"
              height="24"
              style={{ rotate: "-90deg", cursor: "pointer" }}
              onClick={handleGoNextQ3}
            />
            {/* </div> */}
          </div>
        </Modal.Title>
      </Modal>

      <Modal
        show={q4Show}
        onHide={handleQ4Close}
        centered
        className="custom-modalques"
      >
        <div className="text-center mtminmodel">
          <div className="fw-bold text-uppercase fs-4 text-white ftzinput">
            {/* Become an Ambassador */}
            {AmbassadorAll[selectedLanguage]?.BAmbssdAn}
          </div>
          <div
            className="w-100 d-flex mx-auto mawitline"
            style={{
              border: "2px solid #BFBFBF",
              height: "2px",
              marginTop: "25px",
              maxWidth: "420px",
            }}
          >
            <div
              className="d-flex z-4 gap-md-5 gap-sm-4 gap-3   justify-content-center"
              style={{ marginTop: "-12px" }}
            >
              <div
                className="bg-white rounded-circle cirwidhe"
                style={{ width: "22px", height: "22px", marginLeft: "-15px" }}
                // onClick={handleQShow} // Trigger modal on click
              ></div>

              <div
                className="bg-white rounded-circle cirwidhe"
                style={{ width: "22px", height: "22px" }}
                // onClick={handleQ2Show}
              ></div>

              <div
                className="bg-white rounded-circle cirwidhe"
                style={{ width: "22px", height: "22px" }}
                // onClick={handleQ3Show}
              ></div>

              <div
                className={`rounded-circle ${
                  q4Show ? "bgorg" : "bg-white"
                } cirwidhe`}
                style={{ width: "22px", height: "22px" }}
                // onClick={handleQ4Show}
              ></div>

              <div
                className="bg-white rounded-circle cirwidhe"
                style={{ width: "22px", height: "22px" }}
                // onClick={handleQ5Show}
              ></div>

              <div
                className="bg-white rounded-circle cirwidhe"
                style={{ width: "22px", height: "22px" }}
              ></div>

              <div
                className="bg-white rounded-circle cirwidhe"
                style={{ width: "22px", height: "22px" }}
              ></div>
            </div>
          </div>
        </div>
        <Modal.Header className=" border-0">
          {/* <img
            className=" border-0 closebtn "
            src={require("../components/image/close.png")}
            alt="logo"
            onClick={handleQ4Close}
          /> */}
        </Modal.Header>
        <Modal.Title className="text-center  " style={{ marginTop: "35px" }}>
          <div className="d-flex gap-4 justify-content-center mx-5">
            <img
              src={require("./image/arrowback.png")}
              className="mt-2 clasarowmdl"
              width="20"
              height="24"
              onClick={handleGoBackQ4}
              style={{ rotate: "90deg", cursor: "pointer" }}
            />

            <div
              className="fw-bold w-100 ftzinput"
              style={{ maxWidth: "400px ", fontSize: "22px" }}
            >
              {/* Have you led any book clubs in the past? */}
              {AmbassadorAll[selectedLanguage]?.LedText}
            </div>
          </div>
        </Modal.Title>

        <Modal.Body className="mx-auto   mt-5 ">
          <div
            className="d-flex w-100 gap-md-4 gap-2 mt-sm-3 flex-wrap justify-content-center"
            style={{ maxWidth: "390px" }}
          >
            {AmbassadorAll[selectedLanguage]?.YesNoText.map((label, index) => (
              <div
                key={index}
                className="text-center wstyupto ftzcopy fw-bold fs-5 py-1 rounded-1"
                style={{
                  width: "180px",
                  height: "45px",
                  border: "2px solid #000000",
                  backgroundColor:
                    selectedBoxQ4 === index ? "#62C0C7" : "transparent", // Blue background when selected
                  color: selectedBoxQ4 === index ? "#fff" : "#000", // White text when selected
                  cursor: "pointer", // Add cursor pointer for a better UX
                }}
                onClick={() => handleBoxClickQ4(index)} // Handle box click
              >
                {label}
              </div>
            ))}
          </div>
        </Modal.Body>
      </Modal>

      <Modal
        show={q5Show}
        onHide={handleQ5Close}
        centered
        className="custom-modalques  customquset5"
      >
        <div className="text-center mtminmodel">
          <div className="fw-bold text-uppercase fs-4 text-white ftzinput">
            {/* Become an Ambassador */}
            {AmbassadorAll[selectedLanguage]?.BAmbssdAn}
          </div>
          <div
            className="w-100 d-flex mx-auto mawitline"
            style={{
              border: "2px solid #BFBFBF",
              height: "2px",
              marginTop: "25px",
              maxWidth: "420px",
            }}
          >
            <div
              className="d-flex z-4 gap-md-5 gap-sm-4 gap-3   justify-content-center"
              style={{ marginTop: "-12px" }}
            >
              <div
                className="bg-white rounded-circle cirwidhe"
                style={{ width: "22px", height: "22px", marginLeft: "-15px" }}
                // onClick={handleQShow} // Trigger modal on click
              ></div>

              <div
                className="bg-white rounded-circle cirwidhe"
                style={{ width: "22px", height: "22px" }}
                // onClick={handleQ2Show}
              ></div>

              <div
                className="bg-white rounded-circle cirwidhe"
                style={{ width: "22px", height: "22px" }}
                // onClick={handleQ3Show}
              ></div>

              <div
                className="bg-white rounded-circle cirwidhe"
                style={{ width: "22px", height: "22px" }}
                // onClick={handleQ4Show}
              ></div>

              <div
                className={`rounded-circle ${
                  q5Show ? "bgorg" : "bg-white"
                } cirwidhe`}
                style={{ width: "22px", height: "22px" }}
                // onClick={handleQ5Show}
              ></div>

              <div
                className="bg-white rounded-circle cirwidhe"
                style={{ width: "22px", height: "22px" }}
                // onClick={handleQ6Show}
              ></div>
              <div
                className="bg-white rounded-circle cirwidhe"
                style={{ width: "22px", height: "22px" }}
              ></div>
            </div>
          </div>
        </div>
        <Modal.Header className=" border-0">
          {/* <img
            className=" border-0 closebtn "
            src={require("../components/image/close.png")}
            alt="logo"
            onClick={handleQ5Close}
          /> */}
        </Modal.Header>
        <Modal.Title className="text-center  " style={{ marginTop: "26px" }}>
          <div className="d-flex gap-4 justify-content-center mx-5">
            <img
              src={require("./image/arrowback.png")}
              className="mt-2 clasarowmdl"
              width="20"
              height="24"
              onClick={handleGoBackQ5}
              style={{ rotate: "90deg", cursor: "pointer" }}
            />

            <div
              className="fw-bold w-100 text-start ftzinput"
              style={{ maxWidth: "450px ", fontSize: "22px" }}
            >
              {/* Explain in few a lines how you would organize a book club event
              (e.g. location, how to guide the discussion etc) */}
              {AmbassadorAll[selectedLanguage]?.DiscussText}
            </div>

            {/* <div className="mtarowinpt"> */}
            <img
              src={require("./image/arrowback.png")}
              className="mt-2 clasarowmdl"
              width="20"
              height="24"
              style={{ rotate: "-90deg", cursor: "pointer" }}
              onClick={handleGoNextQ6}
            />
            {/* </div> */}
          </div>
        </Modal.Title>

        <Modal.Body className="mx-auto    ">
          <div className="d-flex mb-3  justify-content-center">
            <textarea
              type="text"
              className="form-control mx-4 witsyuto640 fw600 fs-5 bg-transparent ftzevtt rounded-1 placeholder-login"
              id="text"
              style={{
                border: "2px solid #000000",
                width: "480px",
                height: "262px",
                color: "#787878",
              }}
              value={discussionInfo} // ✅ Connect state
              onChange={(e) => setDiscussionInfo(e.target.value)} // ✅ Update state on change
            />
            {/* <div className="mtarowinpt">
              <img
                src={require("./image/arrowback.png")}
                className="clasarowmdl  "
                width="20"
                height="24"
                style={{ rotate: "-90deg", cursor: "pointer" }}
                onClick={handleGoNextQ6}
              />
            </div> */}
          </div>
        </Modal.Body>
      </Modal>

      <Modal
        show={q6Show}
        onHide={handleQ6Close}
        centered
        className="custom-modalques  customquset5"
      >
        <div className="text-center mtminmodel">
          <div className="fw-bold text-uppercase fs-4 text-white ftzinput">
            {/* Become an Ambassador */}
            {AmbassadorAll[selectedLanguage]?.BAmbssdAn}
          </div>
          <div
            className="w-100 d-flex mx-auto mawitline"
            style={{
              border: "2px solid #BFBFBF",
              height: "2px",
              marginTop: "25px",
              maxWidth: "420px",
            }}
          >
            <div
              className="d-flex z-4 gap-md-5 gap-sm-4 gap-3   justify-content-center"
              style={{ marginTop: "-12px" }}
            >
              <div
                className="bg-white rounded-circle cirwidhe"
                style={{ width: "22px", height: "22px", marginLeft: "-15px" }}
                // onClick={handleQShow} // Trigger modal on click
              ></div>

              <div
                className="bg-white rounded-circle cirwidhe"
                style={{ width: "22px", height: "22px" }}
                // onClick={handleQ2Show}
              ></div>

              <div
                className="bg-white rounded-circle cirwidhe"
                style={{ width: "22px", height: "22px" }}
                // onClick={handleQ3Show}
              ></div>

              <div
                className="bg-white rounded-circle cirwidhe"
                style={{ width: "22px", height: "22px" }}
                // onClick={handleQ4Show}
              ></div>

              <div
                className="bg-white rounded-circle cirwidhe"
                style={{ width: "22px", height: "22px" }}
                // onClick={handleQ5Show}
              ></div>

              <div
                className={`rounded-circle ${
                  q6Show ? "bgorg" : "bg-white"
                } cirwidhe`}
                style={{ width: "22px", height: "22px" }}
                // onClick={handleQ6Show}
              ></div>
              <div
                className="bg-white rounded-circle cirwidhe"
                style={{ width: "22px", height: "22px" }}
                // onClick={handleQ7Show}
              ></div>
            </div>
          </div>
        </div>
        <Modal.Header className=" border-0">
          {/* <img
            className=" border-0 closebtn "
            src={require("../components/image/close.png")}
            alt="logo"
            onClick={handleQ6Close}
          /> */}
        </Modal.Header>
        <Modal.Title className="text-center  " style={{ marginTop: "26px" }}>
          <div className="d-flex gap-4 justify-content-center mx-5">
            <img
              src={require("./image/arrowback.png")}
              className="mt-2 clasarowmdl"
              width="20"
              height="24"
              onClick={handleGoBackQ6}
              style={{ rotate: "90deg", cursor: "pointer" }}
            />

            <div
              className="fw-bold w-100 text-start ftzinput"
              style={{ maxWidth: "430px ", fontSize: "22px" }}
            >
              {/* Explain in a few lines why you wish to become an Ofellio
              Ambassador */}
              {AmbassadorAll[selectedLanguage]?.WishText}
            </div>
          </div>
        </Modal.Title>

        <Modal.Body className="mx-auto    ">
          <div className="d-flex   justify-content-center">
            <textarea
              type="text"
              className="form-control  witsyuto640 fw600 fs-5 bg-transparent ftzevtt rounded-1 placeholder-login"
              id="text"
              style={{
                border: "2px solid #000000",
                width: "480px",
                height: "262px",
                color: "#787878",
              }}
              value={wishesInfo} // ✅ Connect state
              onChange={(e) => setWishesInfo(e.target.value)} // ✅ Update state on change
            />
          </div>

          <div className="d-flex justify-content-md-end justify-content-center ">
            <button
              className="w-100 mt-md-4 mt-5 eventcrtwid fw600 fs-5 !text-white text-center rounded-1 border-0"
              style={{
                maxWidth: "162px",
                height: "44px",
                backgroundColor: "#34C3C9",
              }}
              // onClick={handleQ7Show}
              onClick={handleFinish}
            >
              {/* Submit */}
              {AmbassadorAll[selectedLanguage]?.SubmitBtn}
            </button>
          </div>
        </Modal.Body>
      </Modal>

      <Modal
        show={q7Show}
        onHide={handleQ7Close}
        centered
        className="custom-modalmeet modelq7"
        backdrop={true}
      >
        <div className="text-center mtminmodel">
          <div className="fw-bold text-uppercase fs-4 text-white ftzinput">
            {/* Become an Ambassador */}
            {AmbassadorAll[selectedLanguage]?.BAmbssdAn}
          </div>
          <div
            className="w-100 d-flex mx-auto mawitline"
            style={{
              border: "2px solid #BFBFBF",
              height: "2px",
              marginTop: "25px",
              maxWidth: "420px",
            }}
          >
            <div
              className="d-flex z-4 gap-md-5 gap-sm-4 gap-3   justify-content-center"
              style={{ marginTop: "-12px" }}
            >
              <div
                className="bg-white rounded-circle cirwidhe"
                style={{ width: "22px", height: "22px", marginLeft: "-15px" }}
                // onClick={handleQShow} // Trigger modal on click
              ></div>

              <div
                className="bg-white rounded-circle cirwidhe"
                style={{ width: "22px", height: "22px" }}
                // onClick={handleQ2Show}
              ></div>

              <div
                className="bg-white rounded-circle cirwidhe"
                style={{ width: "22px", height: "22px" }}
                // onClick={handleQ3Show}
              ></div>

              <div
                className="bg-white rounded-circle cirwidhe"
                style={{ width: "22px", height: "22px" }}
                // onClick={handleQ4Show}
              ></div>

              <div
                className="bg-white rounded-circle cirwidhe"
                style={{ width: "22px", height: "22px" }}
                // onClick={handleQ5Show}
              ></div>

              <div
                className="bg-white rounded-circle cirwidhe"
                style={{ width: "22px", height: "22px" }}
                // onClick={handleQ6Show}
              ></div>
              <div
                className={`rounded-circle ${
                  q7Show ? "bgorg" : "bg-white"
                } cirwidhe`}
                style={{ width: "22px", height: "22px" }}
                // onClick={handleQ7Show}
              ></div>
            </div>
          </div>
        </div>
        <Modal.Header className=" border-0">
          <img
            className=" border-0 closebtn "
            src={require("../components/image/close.png")}
            style={{ marginTop: "-12px" }}
            alt="logo"
            onClick={handleQ7Close}
          />
        </Modal.Header>
        <Modal.Title className="text-center mt-4 ">
          <img
            src={require("./image/ambasdormodelc.jpg")}
            className="ambasdormodel mx-auto"
            width="107"
            height="74"
          />
        </Modal.Title>
        <Modal.Body className="mx-auto">
          <div
            className="fs-4 fw-bold  text-center w-100 ftzcopy mt-4  "
            style={{ maxWidth: "677px" }}
          >
            {/* Thank you for submitting your answers! */}
            {AmbassadorAll[selectedLanguage]?.LTModel}
          </div>

          <div
            className="fs-4 fw-bold  text-center w-100 ftzcopy mt-4  "
            style={{ maxWidth: "677px" }}
          >
            {/* You will receive a confirmation e-mail soon with the Ambassador's
            guidelines. */}
            {AmbassadorAll[selectedLanguage]?.ReciveText}
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default Ambassadors;
